import { option } from 'fp-ts';
import { isNil as isNilLodash } from 'lodash';
import { isNil } from 'lodash/fp';
import { FeatureFlag } from '@mews-distributor/common';
import { getActiveFeatureFlags } from '@mews/framework';
import { WallClockDateService } from '@mews/framework/datetime';
import { createAction } from '@mews/framework/redux';
import { setStartDate } from '../../actions/dates';
import { setInitialDates } from '../../actions/init';
import { showRates, showSpaceCategories } from '../../actions/navigation';
import { fetchPaymentConfiguration } from '../../api/actions/paymentConfigurations';
import { getCategoryDefaultAgeCategoryOccupancy, getCategoryServiceId } from '../../selectors/views/categories';
import { getMinimalStartDate } from '../../selectors/views/dates';
import { hasSelectedCityDayUseBookableServices } from '../../selectors/views/selectedCityEnterprises';
import { setEnterpriseToolingData } from '../../services/featureFlagService/actions';
import { DistributorPromiseThunkAction, DistributorThunkAction } from '../../types';
import { WithCategoryId, WithEnterpriseId } from '../../types/selectors/selectorPropTypes';
import { logError } from '../../utils/logError';
import { IS_FX_MULTICURRENCY_SERVICE_ENABLED_SET } from '../app/actionTypes';
import { getSelectedCityId } from '../app/selectors';
import { isMultiHotel } from '../configuration/selectors';
import { hasCity } from '../entities/cities/selectors';
import {
    getEnterpriseConfigurationId,
    getEnterpriseDefaultLanguageCode,
    getEnterpriseIsFxMulticurrencyServiceEnabled,
    getEnterpriseName,
    hasEnterpriseData,
} from '../entities/enterprises/selectors';
import { getSelectedEnterpriseBasicProductIds } from '../entities/products/selectors';
import { localizeProperty } from '../globalization/selectors';
import { shouldGetPaymentConfiguration } from '../paymentConfigurations/selectors';
import { fetchCityResourceCategoriesWithCheck } from '../resourceCategories/actions';
import { getCategoryEnterpriseId, isCategoryNil } from '../resourceCategories/selectors';
import { fetchInitialCityServiceAvailabilities } from '../serviceAvailability';
import { logSentryError } from '../../sentry/sentry';
import { updateObservabilityLabels } from '../../observability/actions';
import {
    AGE_CATEGORY_OCCUPANCY_SET,
    CITY_SELECTED,
    DEFAULT_RESOURCE_ORDER_SET,
    HOTEL_SELECTED,
    ROOM_OCCUPANCY_CHANGED,
    ROOM_SELECTED,
    SPACE_TYPE_COUNT_CHANGED,
    SPACE_TYPE_COUNT_RESET,
    SPACE_TYPE_COUNT_SET,
    VOUCHER_CODE_SET,
} from './actionTypes';
import { getInitialRoomOccupanciesFromDateOccupancy } from './occupanciesRoom';
import { SpaceTypeCountSetPayload } from './payloadTypes';
import { getAgeCategoryOccupancyDataAdultChildCounts, getEndDate, getSelectedHotelId, getStartDate } from './selectors';

export function setVoucherCode(voucherCode: string) {
    return createAction(VOUCHER_CODE_SET, { voucherCode });
}

export function trySelectCity(cityId: string): DistributorThunkAction {
    return (dispatch, getState) => {
        if (hasCity(getState(), { cityId })) {
            void dispatch(selectCity(cityId));
        }
    };
}

export function selectCity(cityId: string, { checkStartDate = true } = {}): DistributorPromiseThunkAction {
    return async (dispatch, getState, { appClockService }) => {
        dispatch(createAction(CITY_SELECTED, { cityId }));
        const state = getState();
        const hasDayUses = hasSelectedCityDayUseBookableServices(state, {});
        if (!hasDayUses && !isNil(getStartDate(state)) && getStartDate(state) === getEndDate(state)) {
            dispatch(
                setInitialDates({ shouldSetNonDisabledDates: false, minimalStartDate: null, maximalEndDate: null })
            );
        }
        if (checkStartDate) {
            const startDate = getStartDate(getState());
            if (isNilLodash(startDate)) {
                return;
            }

            const nowUtc = appClockService.getAppNowUtc();
            const minimalStartDate = getMinimalStartDate(getState(), { nowUtc });
            const wallClockStartDate = WallClockDateService.parse(startDate);
            if (WallClockDateService.isLess(wallClockStartDate, minimalStartDate)) {
                dispatch(setStartDate(minimalStartDate.value));
            }

            await dispatch(fetchInitialCityServiceAvailabilities({ cityId }));
            dispatch(
                setInitialDates({ shouldSetNonDisabledDates: true, minimalStartDate: null, maximalEndDate: null })
            );
        }
    };
}

export const setEnterprise =
    (enterpriseId: string): DistributorThunkAction =>
    (dispatch, getState) => {
        if (hasEnterpriseData(getState(), { enterpriseId })) {
            dispatch(createAction(HOTEL_SELECTED, { hotelId: enterpriseId }));

            const shouldFetchPaymentConfiguration = shouldGetPaymentConfiguration(getState(), {});
            if (shouldFetchPaymentConfiguration === true) {
                void dispatch(fetchPaymentConfiguration());
            }

            const isFxMulticurrencyServiceEnabled = getEnterpriseIsFxMulticurrencyServiceEnabled(getState(), {
                enterpriseId,
            });
            dispatch(createAction(IS_FX_MULTICURRENCY_SERVICE_ENABLED_SET, { isFxMulticurrencyServiceEnabled }));

            const productIds = getSelectedEnterpriseBasicProductIds(getState());
            dispatch(createAction(DEFAULT_RESOURCE_ORDER_SET, { productIds }));
        }
    };

export function selectHotel(hotelId: string): DistributorPromiseThunkAction {
    return async (dispatch, getState, { gainsightService, featureFlagService }) => {
        const currentHotelId = getSelectedHotelId(getState());
        if (currentHotelId !== hotelId) {
            await dispatch(setEnterpriseToolingData(hotelId));
            dispatch(setEnterprise(hotelId));
            // We set these values again since we know the hotel for sure
            const bookingEngineId = getEnterpriseConfigurationId(getState(), { enterpriseId: hotelId });
            const enterpriseNames = getEnterpriseName(getState(), { enterpriseId: hotelId });
            const enterpriseDefaultLanguage = getEnterpriseDefaultLanguageCode(getState(), { enterpriseId: hotelId });
            const enterpriseName = localizeProperty(getState(), {
                property: enterpriseNames,
                languageCode: enterpriseDefaultLanguage,
            });

            const featureFlagsActive = getActiveFeatureFlags({ featureFlagService, featureFlagEnum: FeatureFlag });

            gainsightService.setGlobalContext({
                enterpriseId: hotelId,
                bookingEngineId,
                enterpriseName,
                featureFlagsActive,
            });

            updateObservabilityLabels({ enterpriseId: hotelId });
        }

        if (isMultiHotel(getState())) {
            await dispatch(showSpaceCategories());
        }
    };
}

export function trySelectHotel(enterpriseId: string): DistributorPromiseThunkAction {
    return async (dispatch, getState) => {
        if (hasEnterpriseData(getState(), { enterpriseId })) {
            await dispatch(selectHotel(enterpriseId));
        }
    };
}

export const createSpaceTypeCountChangedAction = ({ count }: { count: number }) =>
    createAction(SPACE_TYPE_COUNT_CHANGED, { count });

const setSpaceTypeCount = ({ count, occupancy }: SpaceTypeCountSetPayload) =>
    createAction(SPACE_TYPE_COUNT_SET, { count, occupancy });

const setInitialCategoryOccupancy =
    ({ enterpriseId, categoryId }: WithCategoryId & WithEnterpriseId): DistributorThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const roomOccupancies = getInitialRoomOccupanciesFromDateOccupancy(state, { enterpriseId, categoryId });

        roomOccupancies.forEach((roomOccupancy) => {
            dispatch(setAgeCategoryOccupancy(roomOccupancy));
        });
    };

export const setEnterpriseSpaceTypeCount =
    ({ count, categoryId }: { count: number; categoryId: string }): DistributorThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const enterpriseId = getCategoryEnterpriseId(state, { categoryId });
        const serviceId = getCategoryServiceId(state, { categoryId });

        let occupancy =
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            enterpriseId && serviceId
                ? option.toNullable(
                      getCategoryDefaultAgeCategoryOccupancy(state, { categoryId, enterpriseId, serviceId })
                  )
                : null;

        if (!occupancy) {
            const error = new Error('Cannot set spaceTypeCount. Default occupancy is nil.');
            dispatch(
                logSentryError({
                    error,
                    extra: {
                        categoryId,
                        enterpriseId,
                        serviceId,
                    },
                })
            );
            return;
        }

        occupancy = occupancy.filter((occ) => occ.personCount !== 0);

        dispatch(setSpaceTypeCount({ count, occupancy }));
    };

const spaceTypeCountResetAction = createAction(SPACE_TYPE_COUNT_RESET);

export const resetEnterpriseSpaceTypeCount =
    (categoryId?: string): DistributorThunkAction =>
    (dispatch, getState) => {
        dispatch(spaceTypeCountResetAction);
        const enterpriseId = categoryId != null ? getCategoryEnterpriseId(getState(), { categoryId }) : undefined;

        if (categoryId != null && enterpriseId != null) {
            dispatch(setInitialCategoryOccupancy({ categoryId, enterpriseId }));
        }
    };

export function trySelectRoom(categoryId: string): DistributorPromiseThunkAction {
    return async (dispatch, getState) => {
        const state = getState();
        const cityId = getSelectedCityId(state);
        if (cityId == null) {
            return;
        }
        try {
            await dispatch(fetchCityResourceCategoriesWithCheck(cityId));
        } catch (e) {
            dispatch(
                logSentryError({
                    error: e,
                    extra: { cityId, message: 'Cannot select room. Fetching resource categories failed' },
                })
            );
            return;
        }

        if (isCategoryNil(getState(), { categoryId })) {
            // Category doesn't have to always be found, because categoryId is passed by 3rd party and can be invalid.
            logError(`roomId is invalid: ${categoryId}`);
            return;
        }
        dispatch(selectRoom(categoryId));
    };
}

export function selectRoom(categoryId: string): DistributorThunkAction {
    return (dispatch) => {
        dispatch(createAction(ROOM_SELECTED, { roomId: categoryId }));
        dispatch(
            showRates({
                categoryId,
                onResetEnterpriseSpaceTypeCount: (resetCategoryId: string) => {
                    dispatch(resetEnterpriseSpaceTypeCount(resetCategoryId));
                },
            })
        );
    };
}

export interface CreateRoomOccupancyChangedActionOptions {
    roomIndex: number;
    adultCount: number;
    childCount: number;
}

const createRoomOccupancyChangedAction =
    ({ roomIndex, adultCount, childCount }: CreateRoomOccupancyChangedActionOptions): DistributorThunkAction =>
    (dispatch) => {
        dispatch(createAction(ROOM_OCCUPANCY_CHANGED, { roomIndex, adultCount, childCount }));
    };

interface SetAgeCategoryOccupancyOptions {
    categoryIndex: number;
    ageCategoryId: string;
    personCount: number;
}

export const setAgeCategoryOccupancy =
    ({ categoryIndex, ageCategoryId, personCount }: SetAgeCategoryOccupancyOptions): DistributorThunkAction =>
    (dispatch, getState) => {
        dispatch(
            createAction(AGE_CATEGORY_OCCUPANCY_SET, {
                categoryIndex,
                ageCategoryId,
                personCount,
            })
        );
        const { adultCount, childCount } = getAgeCategoryOccupancyDataAdultChildCounts(getState(), { categoryIndex });

        dispatch(createRoomOccupancyChangedAction({ roomIndex: categoryIndex, adultCount, childCount }));
    };
